import type {
  FinalDocumentRevision,
  RecordingPingTelemetry,
  RecordingStopReason,
} from '@/shared/api/transcriptor';
import { transcriptorApi } from '@/shared/api/transcriptor/transcriptorApi';
import { debug } from '@/shared/lib';

import type { AudioIssueReason } from '@/entities/transcriptor';

import { AudioLevelMonitor } from './AudioLevelMonitor';
import { buildPingTelemetry } from './buildPingTelemetry';
import { ChunkUploader } from './ChunkUploader';
import {
  ACTIVITY_PING_INTERVAL_MS,
  AUDIO_ANALYZER_CONFIG,
  AUDIO_ISSUE_SILENCE_MS,
  AUDIO_LEVEL_POLL_INTERVAL_MS,
  DEFAULT_RECORDING_CONFIG,
  ERROR_CODES,
  SILENCE_RMS_THRESHOLD,
  SUPPORTED_MIME_TYPES,
} from './constants';
import { DevicePresenceWatcher } from './DevicePresenceWatcher';
import { pickSupportedMimeType } from './pickSupportedMimeType';
import {
  AudioChunkPayload,
  AudioRecordingConfig,
  AudioRecordingError,
  RecordingDiagnostic,
  RecordingState,
} from './types';

const log = (...args: unknown[]) => debug('[AudioRecordingService]', ...args);

export type AudioRecordingServiceCallbacks = {
  onUploadIssue?: (hasIssue: boolean) => void;
  onError?: (error: string) => void;
  onStopped?: (stopReason: RecordingStopReason) => void;
  /** Warning only: recording keeps running regardless. `null` clears it. */
  onAudioIssue?: (issue: AudioIssueReason | null) => void;
  /** A chunk the backend acknowledged: the truthful "last saved" time. */
  onChunkUploaded?: (uploadedAt: string, chunkIndex: number) => void;
  onDiagnostic?: (diagnostic: RecordingDiagnostic) => void;
  /** Awaited before the recording is stopped on the server. */
  beforeStop?: () => Promise<FinalDocumentRevision[] | void>;
};

const INITIAL_RECORDING_STATE: RecordingState = {
  isRecording: false,
  isPaused: false,
  consultationId: null,
  fragmentId: null,
  chunkIndex: 0,
  audioLevel: 0,
};

export class AudioRecordingService {
  private config: AudioRecordingConfig;
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private requestDataTimer: number | null = null;
  private lastDataTimestamp = 0;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private uploader: ChunkUploader | null = null;
  private levelMonitor: AudioLevelMonitor | null = null;
  private devicePresenceWatcher: DevicePresenceWatcher | null = null;
  private detachTrackListeners: (() => void) | null = null;
  private activeMimeType: string = DEFAULT_RECORDING_CONFIG.mimeType;
  private callbacks: AudioRecordingServiceCallbacks = {};
  private stopped = false;
  private stopReason: RecordingStopReason = 'user';
  // Capture was torn down after an interruption while the uploader keeps
  // draining; the final chunk is enqueued later by finishInterruptedRecording.
  private interrupted = false;
  private wakeLock: WakeLockSentinel | null = null;
  private activityPingTimer: number | null = null;
  // Prevents multiple chunks from being marked as isLast=true.
  // Once we send the last chunk, we must not send any more.
  private sentLastChunk = false;
  // Set to true when the audio track ends (device disconnected, permission revoked)
  // so we stop treating incoming silent blobs as real activity.
  private trackEnded = false;
  private reportedAudioIssue: AudioIssueReason | null = null;
  private lastSignalTimestamp = 0;

  private state: RecordingState = { ...INITIAL_RECORDING_STATE };

  constructor(config: Partial<AudioRecordingConfig> = {}) {
    this.config = { ...DEFAULT_RECORDING_CONFIG, ...config };
  }

  private checkBrowserSupport(): void {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new AudioRecordingError(
        'Browser does not support audio recording',
        ERROR_CODES.NO_BROWSER_SUPPORT,
      );
    }
  }

  setCallbacks(callbacks: AudioRecordingServiceCallbacks): void {
    this.callbacks = callbacks;
  }

  async initialize(stream: MediaStream): Promise<void> {
    this.checkBrowserSupport();
    this.releaseAudioAnalyzer();
    this.stream = stream;
    this.setupAudioAnalyzer();
  }

  private setupAudioAnalyzer(): void {
    if (!this.stream) {
      return;
    }

    this.audioContext = new AudioContext();
    // Autoplay policy may create the context suspended; a suspended context
    // makes the analyser read permanent silence and falsely trip the
    // silent-mic detection.
    this.ensureAudioContextRunning().catch((error) => {
      log('Failed to resume AudioContext during setup:', error);
    });
    this.analyser = this.audioContext.createAnalyser();
    const source = this.audioContext.createMediaStreamSource(this.stream);

    this.analyser.fftSize = AUDIO_ANALYZER_CONFIG.fftSize;
    this.analyser.smoothingTimeConstant =
      AUDIO_ANALYZER_CONFIG.smoothingTimeConstant;
    this.analyser.minDecibels = AUDIO_ANALYZER_CONFIG.minDecibels;
    this.analyser.maxDecibels = AUDIO_ANALYZER_CONFIG.maxDecibels;

    source.connect(this.analyser);
    const buffer = new ArrayBuffer(this.analyser.frequencyBinCount);
    this.dataArray = new Uint8Array(buffer);
  }

  private async ensureAudioContextRunning(): Promise<void> {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  private releaseAudioAnalyzer(): void {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch((error) => {
        log('Failed to close AudioContext during cleanup:', error);
      });
    }

    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
  }

  getAudioLevel(): number {
    if (!this.analyser || !this.dataArray || !this.state.isRecording) {
      return 0;
    }

    this.analyser.getByteFrequencyData(this.dataArray);
    const average =
      this.dataArray.reduce((sum, value) => sum + value, 0) /
      this.dataArray.length;
    return Math.min(100, (average / 255) * 100);
  }

  /**
   * Time-domain RMS on a 0-100 scale: unlike getAudioLevel() (frequency
   * average feeding the UI visualizer), digital silence reads exactly 0 here
   * while a live mic's noise floor doesn't.
   */
  private getTimeDomainLevel(): number {
    if (!this.analyser || !this.dataArray) {
      return 0;
    }

    this.analyser.getByteTimeDomainData(this.dataArray);
    const sumSquares = this.dataArray.reduce((sum, value) => {
      const deviation = (value - 128) / 128;
      return sum + deviation * deviation;
    }, 0);
    return Math.sqrt(sumSquares / this.dataArray.length) * 100;
  }

  async startRecording(
    consultationId: string,
    fragmentId: string,
  ): Promise<void> {
    if (this.state.isRecording) {
      throw new AudioRecordingError(
        'Recording already in progress',
        ERROR_CODES.INVALID_STATE,
      );
    }

    if (!this.stream) {
      throw new AudioRecordingError(
        'Audio not initialized. Call initialize() first',
        ERROR_CODES.INVALID_STATE,
      );
    }

    // startRecording runs inside the user-gesture call chain, so resume()
    // succeeds here even if the constructor-time attempt was blocked.
    await this.ensureAudioContextRunning().catch((error) => {
      log('Failed to resume AudioContext on start:', error);
    });
    await this.lockScreen();

    this.stopped = false;
    this.stopReason = 'user';
    this.interrupted = false;
    this.sentLastChunk = false;
    this.trackEnded = false;
    this.reportedAudioIssue = null;
    this.lastDataTimestamp = Date.now();
    this.lastSignalTimestamp = Date.now();
    this.state = {
      ...INITIAL_RECORDING_STATE,
      isRecording: true,
      consultationId,
      fragmentId,
    };

    // Dispose any existing uploader from previous recording.
    // This prevents resource leaks if startRecording() is called while
    // a previous uploader is still running (e.g., user rapidly restarts).
    if (this.uploader) {
      this.uploader.dispose();
    }

    // Create and start the uploader
    this.uploader = new ChunkUploader(consultationId, fragmentId);
    this.uploader.setCallbacks({
      onUploadIssue: this.callbacks.onUploadIssue,
      onError: this.callbacks.onError,
      onStopped: this.callbacks.onStopped,
      onChunkUploaded: this.callbacks.onChunkUploaded,
      onDiagnostic: this.callbacks.onDiagnostic,
      beforeStop: this.callbacks.beforeStop,
    });
    this.uploader.start();

    try {
      this.activeMimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: this.activeMimeType,
        audioBitsPerSecond: this.config.audioBitsPerSecond,
      });

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        this.handleDataAvailable(event);
      };

      this.mediaRecorder.onerror = () => {
        this.handleRecorderError();
      };

      this.mediaRecorder.onstop = () => {
        // After an interruption the recorder is stopped to release the mic
        // while the uploader keeps draining; the last chunk (with its reason)
        // is enqueued only when the user asks to generate the transcript.
        if (this.interrupted) {
          this.releaseCapture();
          return;
        }
        this.enqueueLastChunk(this.stopReason);
        this.cleanup();
      };

      const audioTrack = this.stream.getAudioTracks()[0];
      if (audioTrack) {
        this.detachTrackListeners = this.attachTrackListeners(audioTrack);
        this.startDevicePresenceWatcher(audioTrack);
      }

      // Start without timeslice - we'll manually request data
      this.mediaRecorder.start();
      this.levelMonitor = new AudioLevelMonitor(
        () => this.getTimeDomainLevel(),
        AUDIO_LEVEL_POLL_INTERVAL_MS,
        (level) => this.handleLevelSample(level),
      );
      this.levelMonitor.start();
      this.startRequestDataTimer();
      this.startActivityPing();
    } catch (error) {
      this.state.isRecording = false;
      this.uploader?.dispose();
      this.uploader = null;
      throw new AudioRecordingError(
        'Failed to start recording',
        ERROR_CODES.RECORDING_FAILED,
        error,
      );
    }
  }

  private getSupportedMimeType(): string {
    return pickSupportedMimeType(
      SUPPORTED_MIME_TYPES,
      (mimeType) => MediaRecorder.isTypeSupported(mimeType),
      this.config.mimeType,
    );
  }

  /**
   * Device-level signals during recording. When a mic is unplugged, the
   * track fires 'ended' but the MediaRecorder may keep emitting silent blobs
   * with size > 0. 'mute'/'unmute' cover OS-level mute on the platforms that
   * report it; silence detection remains the safety net for the rest.
   */
  private attachTrackListeners(track: MediaStreamTrack): () => void {
    const handleEnded = () => {
      this.trackEnded = true;
      this.callbacks.onDiagnostic?.({ name: 'track_ended' });
      // Programmatic track.stop() does not fire 'ended', so getting here
      // during recording means the device is gone — warn right away instead
      // of waiting for the next chunk boundary.
      if (this.isCapturing()) {
        this.reportAudioIssue('device_lost');
      }
    };
    const handleMute = () => {
      if (this.isCapturing() && !this.trackEnded) {
        this.reportAudioIssue('track_muted');
      }
    };
    const handleUnmute = () => {
      if (this.reportedAudioIssue === 'track_muted') {
        this.reportAudioIssue(null);
      }
    };

    track.addEventListener('ended', handleEnded, { once: true });
    track.addEventListener('mute', handleMute);
    track.addEventListener('unmute', handleUnmute);

    return () => {
      track.removeEventListener('ended', handleEnded);
      track.removeEventListener('mute', handleMute);
      track.removeEventListener('unmute', handleUnmute);
    };
  }

  private startDevicePresenceWatcher(track: MediaStreamTrack): void {
    this.devicePresenceWatcher?.stop();
    this.devicePresenceWatcher = new DevicePresenceWatcher({
      deviceId: track.getSettings().deviceId ?? null,
      onPresenceChange: (present) => {
        if (!this.isCapturing()) {
          return;
        }
        if (!present) {
          this.reportAudioIssue('device_lost');
          return;
        }
        if (this.reportedAudioIssue === 'device_lost' && !this.trackEnded) {
          this.reportAudioIssue(null);
        }
      },
    });
    this.devicePresenceWatcher.start();
  }

  private isCapturing(): boolean {
    return !this.stopped && !this.interrupted;
  }

  private handleDataAvailable(event: BlobEvent): void {
    // A non-working mic still produces blobs with size > 0 that contain only
    // silence, so activity is judged by the audio level, not by blob size.
    const peakLevel = this.levelMonitor?.consumePeak() ?? 0;
    const hasSignal = !this.trackEnded && peakLevel > SILENCE_RMS_THRESHOLD;
    if (hasSignal) {
      this.lastDataTimestamp = Date.now();
    }

    if (event.data.size === 0 && !this.stopped) {
      return;
    }

    if (!this.uploader) {
      return;
    }

    // Guard against sending multiple last chunks.
    // MediaRecorder may fire multiple ondataavailable events after stop(),
    // but we must only mark ONE chunk as isLast=true.
    if (this.sentLastChunk) {
      return;
    }

    const isLast = this.stopped;
    if (isLast) {
      this.sentLastChunk = true;
    }

    const chunk: AudioChunkPayload = {
      audioData: event.data,
      index: this.state.chunkIndex,
      isLast,
      stopReason: isLast ? this.stopReason : undefined,
    };

    this.uploader.addChunk(chunk);
    this.state.chunkIndex++;
  }

  private handleLevelSample(level: number): void {
    if (!this.isCapturing() || this.state.isPaused || this.trackEnded) {
      return;
    }

    if (level > SILENCE_RMS_THRESHOLD) {
      this.lastSignalTimestamp = Date.now();
      if (this.reportedAudioIssue === 'silence') {
        this.reportAudioIssue(null);
      }
      return;
    }

    // A muted or lost device already explains the silence; don't downgrade
    // that message to the generic one.
    if (
      this.reportedAudioIssue === null &&
      Date.now() - this.lastSignalTimestamp >= AUDIO_ISSUE_SILENCE_MS
    ) {
      this.reportAudioIssue('silence');
    }
  }

  private reportAudioIssue(issue: AudioIssueReason | null): void {
    if (this.reportedAudioIssue === issue) {
      return;
    }

    this.reportedAudioIssue = issue;
    this.callbacks.onAudioIssue?.(issue);
  }

  private handleRecorderError(): void {
    log('MediaRecorder error occurred');
    this.callbacks.onDiagnostic?.({ name: 'recorder_error' });
    this.stopRecording().catch((error) => {
      log('Failed to stop recording during error handling:', error);
    });
  }

  private startRequestDataTimer(): void {
    this.stopRequestDataTimer();
    this.requestDataTimer = window.setInterval(() => {
      if (
        this.mediaRecorder &&
        this.mediaRecorder.state === 'recording' &&
        !this.state.isPaused
      ) {
        this.mediaRecorder.requestData();
      }
    }, this.config.chunkInterval);
  }

  private stopRequestDataTimer(): void {
    if (this.requestDataTimer) {
      window.clearInterval(this.requestDataTimer);
      this.requestDataTimer = null;
    }
  }

  pauseRecording(): void {
    if (!this.state.isRecording || this.state.isPaused) {
      return;
    }

    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.pause();
    }

    this.state.isPaused = true;
    this.lastDataTimestamp = Date.now();
    this.stopRequestDataTimer();
    this.levelMonitor?.stop();
  }

  resumeRecording(): void {
    if (!this.state.isRecording || !this.state.isPaused) {
      return;
    }

    if (this.mediaRecorder?.state === 'paused') {
      this.mediaRecorder.resume();
    }

    this.state.isPaused = false;
    this.lastDataTimestamp = Date.now();
    this.lastSignalTimestamp = Date.now();
    // Discard the peak accumulated before the pause.
    this.levelMonitor?.consumePeak();
    this.levelMonitor?.start();
    this.startRequestDataTimer();
  }

  /**
   * Stop recording. This will:
   * 1. Mark as stopped (next chunk will have isLast=true)
   * 2. Request final data from MediaRecorder
   * 3. Stop MediaRecorder
   * 4. The uploader will call /stop endpoint after all chunks are uploaded
   *
   * `stopReason` travels with the last chunk to /stop; `client_interrupted`
   * is for the user finishing early because the audio signal was lost.
   */
  async stopRecording(stopReason: RecordingStopReason = 'user'): Promise<void> {
    if (!this.state.isRecording) {
      return;
    }

    this.stopped = true;
    this.stopReason = stopReason;
    this.callbacks.onDiagnostic?.({
      name: 'stop_requested',
      attributes: { stop_reason: stopReason },
    });
    this.stopRequestDataTimer();
    await this.unlockScreen();

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Request final data before stopping
      this.mediaRecorder.requestData();
      this.mediaRecorder.stop();
    } else {
      // Recorder is already inactive or missing, but we initiated stop.
      // We must ensure the uploader gets the last chunk signal.
      this.enqueueLastChunk(stopReason);
      this.state.isRecording = false;
      this.cleanup();
    }

    this.state.isRecording = false;
  }

  /**
   * The audio track has ended and cannot produce audio again: release the
   * microphone (so the browser's recording indicator is truthful) but keep
   * the uploader draining what was captured. Nothing is marked last yet —
   * that happens in finishInterruptedRecording() when the user decides.
   */
  suspendForInterruption(): void {
    if (!this.state.isRecording || this.interrupted) {
      return;
    }

    this.interrupted = true;
    this.state.isRecording = false;
    this.state.isPaused = false;
    this.callbacks.onDiagnostic?.({ name: 'capture_suspended' });
    this.stopRequestDataTimer();
    this.stopActivityPing();
    this.levelMonitor?.stop();

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // The flushed blob is a regular chunk (stopped stays false); onstop
      // then releases capture.
      this.mediaRecorder.requestData();
      this.mediaRecorder.stop();
      return;
    }
    this.releaseCapture();
  }

  /**
   * Finish an interrupted recording from what the uploader still holds: the
   * final chunk goes out after every queued chunk, and /stop carries the
   * interruption reason. Returns false when there is no live uploader to
   * hand the chunk to (fatal upload error, or capture never suspended), in
   * which case the caller must stop the fragment through the API directly.
   */
  finishInterruptedRecording(): boolean {
    if (!this.interrupted || !this.uploader?.isActive()) {
      return false;
    }

    this.callbacks.onDiagnostic?.({
      name: 'stop_requested',
      attributes: { stop_reason: 'client_interrupted' },
    });
    this.enqueueLastChunk('client_interrupted');
    this.resetState();
    return true;
  }

  private enqueueLastChunk(stopReason: RecordingStopReason): void {
    if (!this.uploader || this.sentLastChunk) {
      return;
    }

    this.sentLastChunk = true;
    this.uploader.addChunk({
      audioData: new Blob([], { type: this.activeMimeType }),
      index: this.state.chunkIndex,
      isLast: true,
      stopReason,
    });
  }

  /**
   * Force stop without waiting for uploads (for error scenarios).
   * Unlike stopRecording(), this immediately aborts the uploader.
   */
  kill(): void {
    this.stopped = true;
    this.sentLastChunk = true; // Prevent any more chunks from being sent
    this.stopRequestDataTimer();
    this.unlockScreen();

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    // Force-abort the uploader - we don't care about pending chunks
    if (this.uploader) {
      this.uploader.dispose();
      this.uploader = null;
    }

    this.state.isRecording = false;
    this.cleanup();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setOnlineStatus(_online: boolean): void {
    // No-op: online status is now handled by ChunkUploader internally
  }

  private async lockScreen(): Promise<void> {
    if (!navigator.wakeLock) {
      log('Wake lock not supported');
      return;
    }

    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
    } catch (error) {
      log('Failed to acquire wake lock:', error);
    }
  }

  private async unlockScreen(): Promise<void> {
    const lock = this.wakeLock;
    this.wakeLock = null;

    if (!lock) {
      return;
    }

    try {
      await lock.release();
    } catch (error) {
      log('Failed to release wake lock:', error);
    }
  }

  private startActivityPing(): void {
    this.stopActivityPing();

    const { consultationId, fragmentId } = this.state;

    if (!consultationId || !fragmentId) {
      return;
    }

    this.activityPingTimer = window.setInterval(async () => {
      try {
        await transcriptorApi.activityPing(
          consultationId,
          fragmentId,
          this.buildTelemetry(),
        );
      } catch (error) {
        log('Activity ping failed:', error);
        this.callbacks.onDiagnostic?.({
          name: 'ping_failed',
          attributes: { error: String(error) },
        });
      }
    }, ACTIVITY_PING_INTERVAL_MS);
  }

  private buildTelemetry(): RecordingPingTelemetry {
    return buildPingTelemetry({
      track: this.stream?.getAudioTracks()[0] ?? null,
      recorderState: this.mediaRecorder?.state ?? null,
      peakLevel: this.levelMonitor?.peekPeak() ?? null,
      lastSignalTimestamp: this.lastSignalTimestamp,
      chunkIndex: this.state.chunkIndex,
      now: Date.now(),
      performanceNow: performance.now(),
      pageVisibility:
        typeof document === 'undefined' ? null : document.visibilityState,
    });
  }

  private stopActivityPing(): void {
    if (this.activityPingTimer) {
      window.clearInterval(this.activityPingTimer);
      this.activityPingTimer = null;
    }
  }

  /**
   * Tear down capture (timers, recorder, mic, analyser) without touching the
   * recording identity or the uploader, so an interrupted recording can still
   * be finished from the queued chunks.
   */
  private releaseCapture(): void {
    this.stopRequestDataTimer();
    this.stopActivityPing();
    this.unlockScreen();

    this.levelMonitor?.stop();
    this.levelMonitor = null;
    this.devicePresenceWatcher?.stop();
    this.devicePresenceWatcher = null;
    this.detachTrackListeners?.();
    this.detachTrackListeners = null;

    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder.onstop = null;
    }

    this.mediaRecorder = null;
    this.releaseAudioAnalyzer();

    // Stop stream tracks before clearing reference
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
    }
    this.stream = null;
    // NOTE: lastDataTimestamp is intentionally NOT reset here.
    // It is reset in startRecording() instead. Keeping the last value
    // allows the interruption detection hook to detect that no data
    // has arrived after an unexpected MediaRecorder stop (e.g. device disconnect).
  }

  private resetState(): void {
    // NOTE: We intentionally do NOT dispose the uploader here.
    // After stopRecording(), the uploader must continue running to finish
    // uploading queued chunks and call /stop endpoint.
    // The uploader is only disposed in kill() or when starting a new recording.
    this.interrupted = false;
    this.state = { ...INITIAL_RECORDING_STATE };
  }

  private cleanup(): void {
    this.releaseCapture();
    this.resetState();
  }

  getState(): Readonly<RecordingState> {
    return { ...this.state };
  }

  isRecording(): boolean {
    return this.state.isRecording;
  }

  isPaused(): boolean {
    return this.state.isPaused;
  }

  getLastDataTimestamp(): number {
    return this.lastDataTimestamp;
  }

  hasTrackEnded(): boolean {
    return this.trackEnded;
  }
}
